import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import { type MarketingRouterSignificanceQuery, getMarketingRouterSignificance } from '~/services/PromoService';

export const marketingRouterSignificanceQueryKeys = {
  all: ['marketing-router-significance'] as const,
  detail: (marketingRouterId: string, query: MarketingRouterSignificanceQuery) =>
    [...marketingRouterSignificanceQueryKeys.all, marketingRouterId, query] as const,
};

export function useMarketingRouterSignificance({
  marketingRouterId,
  query,
  enabled = true,
}: {
  marketingRouterId: string | undefined;
  query: MarketingRouterSignificanceQuery;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: marketingRouterSignificanceQueryKeys.detail(marketingRouterId ?? '', query),
    queryFn: async ({ signal }) => {
      if (!marketingRouterId) {
        throw new Error('marketingRouterId is required');
      }
      const response = await getMarketingRouterSignificance(marketingRouterId, query, { signal });
      return response.result;
    },
    enabled: enabled && Boolean(marketingRouterId),
    // The results page re-queries under a different product scope; keep the
    // previous scope's numbers on screen until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

/**
 * The same results for several rules at once, for the side-by-side comparison.
 *
 * Each rule keeps its own request and its own cache entry, under the same key
 * the detail page uses — so a rule already opened is compared without a second
 * fetch, and nothing here merges two rules' numbers.
 */
export function useMarketingRouterSignificances({
  rules,
  loyaltyOfferTypes,
}: {
  rules: Array<{ marketingRouterId: string; ruleId: string }>;
  loyaltyOfferTypes: string;
}) {
  return useQueries({
    queries: rules.map(({ marketingRouterId, ruleId }) => {
      const query: MarketingRouterSignificanceQuery = { ruleId, loyaltyOfferTypes };

      return {
        queryKey: marketingRouterSignificanceQueryKeys.detail(marketingRouterId, query),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const response = await getMarketingRouterSignificance(marketingRouterId, query, { signal });
          return response.result;
        },
      };
    }),
  });
}
